import { useCallback } from 'react';
import { BitriseProfile } from '@trace/types';
import { parse, serialize } from 'cookie';
import useAuthentication, { AuthenticationState } from './useAuthentication';
import { NS_BITRISE_API_TOKEN, NS_BITRISE_CSRF_TOKEN } from '../../constants/LocalStorage';
import { callBitriseApi, callBitriseApp } from '../../fetch/fetch';

export const BITRISE_API_TOKEN_EXPIRY = 60 * 60 * 2;

export default function useAuthenticationBitrise(): AuthenticationState<BitriseProfile> {
  // Gets the users profile data from the public Bitrise API.
  const getData = useCallback(
    async (token: string) =>
      (await callBitriseApi<{ data: BitriseProfile }>('GET', '/me', token)).data,
    [],
  );

  // Fetches a new public API token from the main Bitrise App.
  const getNewToken = useCallback(
    async () =>
      (
        await callBitriseApp<{ token: string }>(
          'POST',
          '/me/profile/security/user_auth_tokens',
          null,
          {
            description: 'Trace - token request',
            expire_in_seconds: BITRISE_API_TOKEN_EXPIRY,
            registration_type: 'login',
          },
          {
            credentials: 'include',
            headers: {
              'x-csrf-token': parse(document.cookie)[NS_BITRISE_CSRF_TOKEN],
            },
          },
        )
      ).token,
    [],
  );

  // Retrieves the Bitrise token from cookies.
  const getStoredToken = useCallback(() => parse(document.cookie)[NS_BITRISE_API_TOKEN], []);

  // Sets the public API token inside a cookie.
  const setToken = useCallback(async (token: string) => {
    document.cookie = serialize(NS_BITRISE_API_TOKEN, token, {
      domain: process.env.APM_DOMAIN,
      expires: new Date(Date.now() + BITRISE_API_TOKEN_EXPIRY * 1000),
      path: '/',
    });
  }, []);

  // Clears the Bitrise token
  const clearToken = () => {
    document.cookie = serialize(NS_BITRISE_API_TOKEN, '', {
      domain: process.env.APM_DOMAIN,
      expires: new Date(1),
      path: '/',
    });
  };

  const onAuthFailed = useCallback(() => {
    window.location.replace(
      `${process.env.BITRISE_APP_AUTH_URL}/users/sign_in?sign_in_redirect=${encodeURIComponent(
        window.location.pathname,
      )}`,
    );
  }, []);

  return useAuthentication({
    clearToken,
    getData,
    getNewToken,
    getStoredToken,
    onAuthFailed,
    setToken,
  });
}
